import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { findMatch, removeEmpty } from '@/utils/resultsHelper';
import { endpoints } from '@/constants/urls';
import { readStates, states } from '@/constants/lifecycle';

const makeSelectImageFetcher = (codes) => createSelector(({ themes: { images } }) => images, (images) => {
  const itemsWithStatus = codes.map(({ code, local }) => {
    const { readStatus } = findMatch(images, code) || { ...readStates };
    return { code, readStatus, local };
  });
  const items = removeEmpty(itemsWithStatus.map(({ code, local, readStatus }) => {
    switch (readStatus) {
      case states.SUCCESS: return { src: `${endpoints.API_IMAGES}${code}` };
      case states.ERROR: return { src: local };
      default: return null;
    }
  }));
  return { images: items, src: items[0]?.src, readStatus: itemsWithStatus.map(({ readStatus }) => readStatus) };
});

const useImageFetcher = ({ codes }) => {
  const selector = useMemo(() => makeSelectImageFetcher(codes), [codes]);
  return useSelector(selector);
};

const useTheme = () => {
  const { theme } = useSelector(({ themes }) => themes);
  return { theme };
};

const useSaveThemeDialog = () => {
  const {
    name, enabled, updateErrors, updateStatus, description,
  } = useSelector(({ themes: { activeTheme } }) => activeTheme);
  return {
    description, enabled, name, updateErrors, updateStatus,
  };
};

const useThemeDialogs = () => {
  const { role: { permissions: { themes: { update } } } } = useSelector(({ auth }) => auth);
  const { activeTheme } = useSelector(({ themes }) => themes);
  return { allowSave: activeTheme.open && update };
};

const useThemesActions = () => {
  const { role: { permissions: { themes: { update } } } } = useSelector(({ auth }) => auth);
  const { themes: { readStatus } } = useSelector(({ themes }) => themes);
  return { allowRefresh: update, readStatus };
};

const useThemesDropdown = () => {
  const { themes: { ribbon: { results, readStatus } }, theme } = useSelector(({ themes }) => themes);
  return { readStatus, theme, themes: results };
};

const useThemesFilters = ({ category }) => {
  const { filter, readErrors } = useSelector(({ themes: { themes } }) => themes[category]);
  return { filterTerm: filter, readErrors };
};

const themesPanelState = (themes, category, update) => {
  const item = themes[category];
  return {
    item,
    state: {
      canEdit: update,
      pageSize: item.pageSize,
      readErrors: item.readErrors,
      readStatus: item.readStatus,
      themes: item.results,
      total: item.count,
    },
  };
};

const makeSelectThemesPanelContainer = (category) => createSelector(
  ({ auth: { role: { permissions: { themes: { update } } } } }) => update,
  ({ themes: { themes } }) => themes,
  (update, themes) => themesPanelState(themes, category, update).state,
);

const useThemesPanelContainer = ({ category }) => {
  const selector = useMemo(() => makeSelectThemesPanelContainer(category), [category]);
  return useSelector(selector);
};

const makeSelectThemesPanel = (category, displayAs) => createSelector(
  ({ auth: { role: { permissions: { themes: { update } } } } }) => update,
  ({ themes: { themes } }) => themes,
  (update, themes) => {
    const { item, state } = themesPanelState(themes, category, update);
    return {
      ...state,
      currentPage: item.currentPage,
      filterTerm: item.filter,
      display: displayAs || item.display,
      pageCount: item.totalPages,
    };
  },
);

const useThemesPanel = ({ category, displayAs }) => {
  const selector = useMemo(() => makeSelectThemesPanel(category, displayAs), [category, displayAs]);
  return useSelector(selector);
};

export default {
  useImageFetcher,
  useSaveThemeDialog,
  useTheme,
  useThemeDialogs,
  useThemesActions,
  useThemesDropdown,
  useThemesFilters,
  useThemesPanelContainer,
  useThemesPanel,
};
